import React, { useState, useEffect, useCallback } from 'react';
import {
  FaPlus,
  FaTag,
  FaClock,
  FaEdit,
  FaTrash,
  FaSpinner,
  FaExclamationTriangle,
  FaPercent,
  FaRupeeSign,
  FaShoppingCart,
  FaUtensils,
  FaToggleOn,
  FaToggleOff,
  FaCheckCircle,
  FaTimesCircle,
  FaCalendarAlt,
  FaInfoCircle,
  FaArrowLeft,
  FaArrowRight,
  FaSave,
  FaTimes,
  FaChevronDown,
  FaGift
} from 'react-icons/fa';

import { getData, postData, putData } from '../../../Services/Fetchnodeservices';

const AddOffer = ({ isDark = false, onSuccess, onCancel, restaurantId }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    offer_name: '',
    offer_type: 'flat',
    offer_scope: 'product',
    offer: '',
    min_cart_value: '0',
    product_id: '',
    product_name: '',
    offer_start: '',
    offer_end: '',
    status: 1,
    buy_quantity: '1',
    free_quantity: '0',
    item_free: null
  });

  const getRestaurantId = useCallback(() => {
    if (restaurantId) return restaurantId;
    try {
      const userDataStr = localStorage.getItem('userData');
      if (userDataStr) {
        const userData = JSON.parse(userDataStr);
        const id = userData?.id || userData?.restaurantId || userData?._id;
        if (id) return id;
      }
      const directId = localStorage.getItem('restaurantId');
      if (directId) return directId;
      const userId = localStorage.getItem('userId');
      if (userId) return userId;
      return null;
    } catch (e) {
      console.error('Error getting restaurantId:', e);
      return null;
    }
  }, [restaurantId]);

  const fetchMenu = useCallback(async () => {
    const id = getRestaurantId();
    if (!id) return;

    try {
      setLoading(true);
      const result = await getData(`menu/menu/${id}`);
      if (result.success) {
        const menuData = result.menu || result.data || [];
        setMenuItems(menuData);
      }
    } catch (error) {
      console.error('Error fetching menu:', error);
    } finally {
      setLoading(false);
    }
  }, [getRestaurantId]);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  const dropdownData = menuItems.map(item => ({
    label: `${item.name} - ₹${item.price || item.menu_price || 0}`,
    value: item.product_id || item.id,
    fullItem: item,
  }));

  const filteredDropdownData = dropdownData.filter(item =>
    item.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    });
  };

  const handleProductSelect = (item) => {
    setFormData({ ...formData, product_id: item.value, product_name: item.fullItem.name });
    setDropdownOpen(false);
    setSearchTerm('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const id = getRestaurantId();
    if (!id) {
      alert('Restaurant ID not found. Please login again.');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        designation_id: id,
        offer: parseFloat(formData.offer) || 0,
        min_cart_value: parseFloat(formData.min_cart_value) || 0,
        buy_quantity: parseInt(formData.buy_quantity) || 1,
        free_quantity: parseInt(formData.free_quantity) || 0,
        status: 1,
        hide: 0
      };

      const result = await postData(`menu/offer/create`, payload);
      if (result.success) {
        alert('Campaign created successfully!');
        if (onSuccess) onSuccess();
      } else {
        alert(result.message || 'Failed to create campaign');
      }
    } catch (err) {
      console.error('❌ Create campaign error:', err);
      alert('Error creating campaign');
    } finally {
      setSubmitting(false);
    }
  };

  // 🎨 ZAPTODOOR ORANGE THEME & COMPACT HORIZONTAL STYLES
  const theme = {
    bg: isDark ? '#0f172a' : '#fff7ed',
    cardBg: isDark ? '#1e293b' : '#ffffff',
    text: isDark ? '#f8fafc' : '#0f172a',
    textSecondary: isDark ? '#94a3b8' : '#64748b',
    border: isDark ? 'rgba(249, 115, 22, 0.2)' : '#fed7aa',
    inputBg: isDark ? '#0f172a' : '#ffffff',
    primary: '#f97316',
    primaryLight: '#fb923c',
    primaryGradient: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)',
    shadow: isDark ? '0 8px 24px rgba(0,0,0,0.4)' : '0 8px 24px rgba(249, 115, 22, 0.1)',
  };

  const styles = {
    container: {
      height: '100vh', // 👈 Fixed height to prevent page scroll
      width: '100%',
      maxWidth: '1000px',
      margin: '0 auto',
      padding: '16px 24px',
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      backgroundColor: theme.bg,
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      overflow: 'hidden', // 👈 No scrolling on main container
      transition: 'all 0.3s ease',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0,
    },
    title: {
      fontSize: '22px',
      fontWeight: 800,
      color: theme.text,
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      margin: 0,
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      borderRadius: '10px',
      border: `1px solid ${theme.border}`,
      background: theme.cardBg,
      color: theme.text,
      fontSize: '14px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    card: {
      background: theme.cardBg,
      borderRadius: '20px',
      padding: '24px',
      border: `1px solid ${theme.border}`,
      boxShadow: theme.shadow,
      flex: 1, // 👈 Takes remaining space
      overflowY: 'auto', // 👈 Internal scroll ONLY if absolutely needed
      transition: 'all 0.3s ease',
    },
    // 👇 HORIZONTAL GRID LAYOUT
    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)', // 2 columns horizontally
      gap: '16px',
    },
    fullWidth: {
      gridColumn: '1 / -1', // Spans both columns
    },
    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
    },
    formLabel: {
      fontSize: '12px',
      fontWeight: 700,
      color: theme.textSecondary,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    formInput: {
      width: '100%',
      height: '40px', // 👈 Compact height
      padding: '0 14px',
      borderRadius: '10px',
      border: `1.5px solid ${theme.border}`,
      backgroundColor: theme.inputBg,
      color: theme.text,
      fontSize: '14px',
      fontWeight: 500,
      transition: 'all 0.2s ease',
      boxSizing: 'border-box',
      outline: 'none',
    },
    offerTypeGroup: {
      display: 'flex',
      gap: '8px',
      flexWrap: 'wrap',
    },
    offerTypeButton: (isActive) => ({
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '6px',
      padding: '8px 12px',
      height: '40px',
      borderRadius: '10px',
      border: `1.5px solid ${isActive ? theme.primary : theme.border}`,
      background: isActive ? 'rgba(249, 115, 22, 0.1)' : 'transparent',
      fontSize: '13px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      color: isActive ? theme.primary : theme.textSecondary,
    }),
    submitButton: {
      width: '100%',
      height: '44px',
      borderRadius: '12px',
      border: 'none',
      background: theme.primaryGradient,
      color: '#ffffff',
      fontSize: '15px',
      fontWeight: 700,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      boxShadow: '0 4px 16px rgba(249, 115, 22, 0.3)',
    },
    dropdownWrapper: {
      position: 'relative',
    },
    dropdownInput: {
      width: '100%',
      height: '40px',
      padding: '0 14px',
      borderRadius: '10px',
      border: `1.5px solid ${theme.border}`,
      backgroundColor: theme.inputBg,
      color: theme.text,
      fontSize: '14px',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxSizing: 'border-box',
    },
    dropdownMenu: {
      position: 'absolute',
      top: 'calc(100% + 4px)',
      left: 0,
      right: 0,
      backgroundColor: theme.cardBg,
      borderRadius: '10px',
      border: `1px solid ${theme.border}`,
      boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
      maxHeight: '180px',
      overflowY: 'auto',
      zIndex: 1000,
    },
    dropdownSearch: {
      width: '100%',
      height: '36px',
      padding: '0 14px',
      border: 'none',
      borderBottom: `1px solid ${theme.border}`,
      backgroundColor: 'transparent',
      color: theme.text,
      fontSize: '13px',
      outline: 'none',
      boxSizing: 'border-box',
    },
    dropdownItem: {
      padding: '8px 14px',
      cursor: 'pointer',
      fontSize: '13px',
      color: theme.text,
      borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.05)' : '#f1f5f9'}`,
      transition: 'background 0.2s',
    },
    selectedProduct: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '6px 12px',
      backgroundColor: isDark ? 'rgba(249, 115, 22, 0.1)' : '#fff7ed',
      borderRadius: '8px',
      marginTop: '6px',
      fontSize: '13px',
      color: theme.primary,
      fontWeight: 600,
    },
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <h2 style={styles.title}>
          <FaGift color={theme.primary} /> Create New Campaign
        </h2>
        <button 
          style={styles.backButton} 
          onClick={onCancel}
          onMouseEnter={(e) => e.currentTarget.style.background = theme.hoverBg}
          onMouseLeave={(e) => e.currentTarget.style.background = theme.cardBg}
        >
          <FaArrowLeft /> Back
        </button>
      </div>

      {/* Compact Form Card */}
      <div style={styles.card}>
        <form onSubmit={handleSubmit} style={styles.formGrid}>
          
          {/* Campaign Name (Full Width) */}
          <div style={{...styles.formGroup, ...styles.fullWidth}}>
            <label style={styles.formLabel}>Campaign Name *</label>
            <input
              type="text"
              name="offer_name"
              placeholder="e.g. Weekend Special 20% OFF"
              style={styles.formInput}
              value={formData.offer_name}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Offer Scope (2 Columns) */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Offer Applies To *</label>
            <div style={styles.offerTypeGroup}>
              {['product', 'cart'].map((scope) => (
                <button
                  key={scope}
                  type="button"
                  style={styles.offerTypeButton(formData.offer_scope === scope)}
                  onClick={() => setFormData({...formData, offer_scope: scope, product_id: '', product_name: '', min_cart_value: '0'})}
                >
                  {scope === 'product' ? <FaUtensils size={14} /> : <FaShoppingCart size={14} />}
                  {scope.charAt(0).toUpperCase() + scope.slice(1)}
                </button>
              ))}
            </div>
          </div>

          {/* Offer Type (2 Columns) */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Offer Type *</label>
            <div style={styles.offerTypeGroup}>
              {['flat', 'percent', 'bogo'].map((type) => (
                <button
                  key={type}
                  type="button"
                  style={styles.offerTypeButton(formData.offer_type === type)}
                  onClick={() => setFormData({...formData, offer_type: type})}
                >
                  {type === 'percent' && <FaPercent size={14} />}
                  {type === 'flat' && <FaRupeeSign size={14} />}
                  {type === 'bogo' && <FaTag size={14} />}
                  {type.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* Offer Value */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              {formData.offer_type === 'percent' ? 'Discount % *' : 
               formData.offer_type === 'flat' ? 'Discount Amount (₹) *' : 'Buy Quantity *'}
            </label>
            <input
              type="number"
              name="offer"
              placeholder={formData.offer_type === 'percent' ? 'e.g. 20' : formData.offer_type === 'flat' ? 'e.g. 100' : 'e.g. 1'}
              style={styles.formInput}
              value={formData.offer}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Conditional: BOGO Free Qty OR Min Cart Value */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>
              {formData.offer_type === 'bogo' ? 'Free Quantity' : 
               formData.offer_scope === 'cart' ? 'Minimum Cart Value (₹)' : 'Not Applicable'}
            </label>
            {formData.offer_type === 'bogo' ? (
              <input
                type="number"
                name="free_quantity"
                placeholder="e.g. 1"
                style={styles.formInput}
                value={formData.free_quantity}
                onChange={handleInputChange}
              />
            ) : formData.offer_scope === 'cart' ? (
              <input
                type="number"
                name="min_cart_value"
                placeholder="e.g. 500"
                style={styles.formInput}
                value={formData.min_cart_value}
                onChange={handleInputChange}
              />
            ) : (
              <div style={{...styles.formInput, display: 'flex', alignItems: 'center', color: theme.textSecondary, fontSize: '13px'}}>
                <FaInfoCircle style={{marginRight: '8px'}} /> N/A for this selection
              </div>
            )}
          </div>

          {/* Product Dropdown (Only if scope is product) */}
          {formData.offer_scope === 'product' && (
            <div style={{...styles.formGroup, ...styles.fullWidth}}>
              <label style={styles.formLabel}>Select Product *</label>
              <div style={styles.dropdownWrapper}>
                <div
                  style={styles.dropdownInput}
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  <span style={{ color: formData.product_name ? theme.text : theme.textSecondary }}>
                    {formData.product_name || (loading ? 'Loading products...' : 'Select a product...')}
                  </span>
                  <FaChevronDown size={14} color={theme.textSecondary} />
                </div>

                {dropdownOpen && (
                  <div style={styles.dropdownMenu}>
                    <input
                      type="text"
                      placeholder="Search products..."
                      style={styles.dropdownSearch}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                    />
                    {loading ? (
                      <div style={{...styles.dropdownItem, display: 'flex', alignItems: 'center', gap: '8px'}}>
                        <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> Loading...
                      </div>
                    ) : filteredDropdownData.length === 0 ? (
                      <div style={styles.dropdownItem}>No products found</div>
                    ) : (
                      filteredDropdownData.map((item) => (
                        <div
                          key={item.value}
                          style={styles.dropdownItem}
                          onClick={() => handleProductSelect(item)}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? 'rgba(249, 115, 22, 0.1)' : '#fff7ed'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                        >
                          {item.label}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
              {formData.product_name && (
                <div style={styles.selectedProduct}>
                  <FaCheckCircle size={14} />
                  <span style={{flex: 1}}>{formData.product_name}</span>
                  <FaTimes 
                    size={14} 
                    style={{cursor: 'pointer', color: '#ef4444'}} 
                    onClick={() => setFormData({...formData, product_id: '', product_name: ''})} 
                  />
                </div>
              )}
            </div>
          )}

          {/* Dates (2 Columns) */}
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>Start Date</label>
            <input
              type="date"
              name="offer_start"
              style={styles.formInput}
              value={formData.offer_start}
              onChange={handleInputChange}
            />
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>End Date</label>
            <input
              type="date"
              name="offer_end"
              style={styles.formInput}
              value={formData.offer_end}
              onChange={handleInputChange}
            />
          </div>

          {/* Submit Button (Full Width) */}
          <div style={{...styles.fullWidth, marginTop: '8px'}}>
            <button
              type="submit"
              style={{
                ...styles.submitButton,
                opacity: submitting ? 0.7 : 1,
                cursor: submitting ? 'not-allowed' : 'pointer'
              }}
              disabled={submitting}
              onMouseEnter={(e) => { if(!submitting) e.currentTarget.style.transform = 'translateY(-1px)' }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)' }}
            >
              {submitting ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaSave />} 
              {submitting ? 'Creating Campaign...' : 'Create Campaign'}
            </button>
          </div>

        </form>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        /* Custom scrollbar for dropdown */
        .dropdownMenu::-webkit-scrollbar { width: 6px; }
        .dropdownMenu::-webkit-scrollbar-track { background: transparent; }
        .dropdownMenu::-webkit-scrollbar-thumb { background: ${theme.primary}; border-radius: 3px; }
      `}</style>
    </div>
  );
};

export default AddOffer;